import { restoreVariantStock, splitVariantItems, fetchCambiosTalle, redistribuirPorCambiosTalle } from './variantStock.js';

export const DEFAULT_STORE_LOGISTICS_CONFIG = {
  request_date_enabled: false,
  request_date_label: 'Dia del pedido',
  request_date_helper: 'Define el dia operativo del pedido desde el calendario logistico.',
  // Aviso libre sobre envios que el comercio le muestra a sus clientes en el checkout.
  shipping_notice_enabled: false,
  shipping_notice_title: '',
  shipping_notice_text: '',
};

export const SHIPPING_NOTICE_TITLE_MAX = 60;
export const SHIPPING_NOTICE_TEXT_MAX = 500;

const isPlainObject = (value) =>
  value != null && typeof value === 'object' && !Array.isArray(value);

export const normalizeStoreLogisticsConfig = (value) => ({
  ...DEFAULT_STORE_LOGISTICS_CONFIG,
  ...(isPlainObject(value) ? value : {}),
});

export const normalizeStoreThemeConfig = (themeConfig) => {
  const safeTheme = isPlainObject(themeConfig) ? themeConfig : {};

  return {
    ...safeTheme,
    logistics: normalizeStoreLogisticsConfig(safeTheme.logistics),
  };
};

export const getStoreLogisticsConfig = (storeConfig) =>
  normalizeStoreThemeConfig(storeConfig?.theme_config).logistics;

/**
 * Aviso de envios configurable por el comercio.
 * Devuelve { enabled, title, text }. `enabled` solo es true si ademas hay texto:
 * un aviso activado pero vacio no se muestra.
 */
export const getStoreShippingNotice = (storeConfig) => {
  const logistics = getStoreLogisticsConfig(storeConfig);
  const title = String(logistics?.shipping_notice_title || '').trim().slice(0, SHIPPING_NOTICE_TITLE_MAX);
  const text = String(logistics?.shipping_notice_text || '').trim().slice(0, SHIPPING_NOTICE_TEXT_MAX);

  return {
    enabled: Boolean(logistics?.shipping_notice_enabled) && text.length > 0,
    title,
    text,
  };
};

export const getLocalYMD = (input = new Date()) => {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '';

  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
};

export const extractRequestedOrderDate = (order) => {
  const raw =
    order?.shipping_address?.requested_date ||
    order?.shipping_address?.requested_delivery_date ||
    order?.customer_snapshot?.requested_date ||
    '';

  const normalized = String(raw || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : '';
};

export const resolveOrderLogisticsDate = (order) =>
  extractRequestedOrderDate(order) || getLocalYMD(order?.created_at || new Date());

export const resolveProductionOperationalDate = (row) => {
  const normalized = String(row?.operational_date || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;
  return getLocalYMD(row?.fecha || new Date());
};

export const formatRequestedOrderDate = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return 'Sin definir';

  const date = new Date(`${normalized}T12:00:00`);
  if (Number.isNaN(date.getTime())) return normalized;

  return date.toLocaleDateString('es-AR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
};

const normalizeStoreSlug = (value) => String(value || '').trim().toLowerCase();
const normalizeStoreOrderOrigin = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (['pos', 'punto_venta', 'punto-de-venta', 'punto de venta'].includes(normalized)) return 'pos';
  if (['web', 'tienda', 'tienda_online', 'tienda-online', 'online'].includes(normalized)) return 'web';
  return normalized;
};
export const normalizeStoreOrderStatus = (value) => String(value || '').trim().toLowerCase();
export const isStoreOrderDeliveredStatus = (value) =>
  ['entregado', 'retirado'].includes(normalizeStoreOrderStatus(value));
export const isStoreOrderCanceledStatus = (value) =>
  normalizeStoreOrderStatus(value) === 'cancelado';
export const isStoreOrderClosedStatus = (value) =>
  isStoreOrderDeliveredStatus(value) || isStoreOrderCanceledStatus(value);

const resolveJoinedSale = (order) => {
  const candidates = [order?.sale, order?.sales];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      if (candidate[0]) return candidate[0];
      continue;
    }

    if (candidate && typeof candidate === 'object') return candidate;
  }

  return null;
};

export const getStoreOrderOrigin = (order) => {
  const explicitOrigin = [
    order?.customer_snapshot?.order_origin,
    order?.shipping_address?.order_origin,
    order?.order_origin,
  ]
    .map(normalizeStoreOrderOrigin)
    .find(Boolean);

  if (explicitOrigin) return explicitOrigin;

  const joinedSale = resolveJoinedSale(order);
  const billingSource = String(joinedSale?.billing_source || order?.billing_source || '').trim().toLowerCase();
  if (billingSource.includes('pos')) return 'pos';

  return 'web';
};

export const isStoreOrderFromPos = (order) => getStoreOrderOrigin(order) === 'pos';
export const isStoreOrderFromWeb = (order) => !isStoreOrderFromPos(order);
/**
 * Canal del pedido, solo para mostrar. OJO: getStoreOrderOrigin NO distingue
 * Tienda Nube a proposito — sigue devolviendo 'web' para esos pedidos, porque
 * saleStoreOrders.js compara `=== "web"` de forma estricta para decidir el
 * asiento del pedido. Meter un tercer valor ahi haria que un pedido de Tienda
 * Nube dejara de contabilizarse, en silencio.
 */
export const getStoreOrderChannel = (order) => {
  const channel = String(order?.channel || '').trim().toLowerCase();
  if (channel === 'tiendanube') return 'tiendanube';
  return isStoreOrderFromPos(order) ? 'pos' : 'web';
};

export const getStoreOrderOriginLabel = (order) => {
  const channel = getStoreOrderChannel(order);
  if (channel === 'tiendanube') return 'TIENDA NUBE';
  return channel === 'pos' ? 'POS' : 'WEB';
};

export const buildStoreSiteContext = ({
  storeConfigs = [],
  defaultFulfillmentSiteId = null,
  organizationDefaultSiteId = null,
} = {}) => ({
  defaultSiteIdByStoreSlug: new Map(
    (Array.isArray(storeConfigs) ? storeConfigs : [])
      .map((config) => [
        normalizeStoreSlug(config?.slug),
        config?.default_fulfillment_site_id || null,
      ])
      .filter(([slug]) => slug),
  ),
  defaultFulfillmentSiteId: defaultFulfillmentSiteId || null,
  organizationDefaultSiteId: organizationDefaultSiteId || null,
});

export const fetchStoreSiteContext = async ({ supabase, organizationId }) => {
  const [storeConfigsRes, organizationRes] = await Promise.all([
    supabase
      .from('store_config')
      .select('slug, default_fulfillment_site_id')
      .eq('organization_id', organizationId),
    supabase
      .from('organizations')
      .select('default_site_id')
      .eq('id', organizationId)
      .maybeSingle(),
  ]);

  if (storeConfigsRes.error) {
    return { data: buildStoreSiteContext(), error: storeConfigsRes.error };
  }

  if (organizationRes.error) {
    return { data: buildStoreSiteContext(), error: organizationRes.error };
  }

  return {
    data: buildStoreSiteContext({
      storeConfigs: storeConfigsRes.data || [],
      organizationDefaultSiteId: organizationRes.data?.default_site_id || null,
    }),
    error: null,
  };
};

export const resolveStoreOrderSiteId = (order, siteContext = {}) => {
  const sale = resolveJoinedSale(order);
  const saleSiteId = sale?.site_id || order?.sale_site_id || null;
  if (saleSiteId) return saleSiteId;

  if (order?.logistics_site_id) return order.logistics_site_id;

  const slug = normalizeStoreSlug(order?.store_slug);
  return (
    siteContext?.defaultSiteIdByStoreSlug?.get?.(slug) ||
    siteContext?.defaultFulfillmentSiteId ||
    siteContext?.organizationDefaultSiteId ||
    null
  );
};

export const reverseStoreOrderStock = async ({
  supabase,
  organizationId,
  order,
  siteContext = null,
}) => {
  if (!order?.sale_id) {
    return { data: { siteId: null, restoredItems: 0, skipped: true }, error: null };
  }

  const resolvedSiteContext =
    siteContext ||
    (await fetchStoreSiteContext({
      supabase,
      organizationId,
    })).data;

  const siteId = resolveStoreOrderSiteId(order, resolvedSiteContext);
  if (!siteId) {
    return {
      data: { siteId: null, restoredItems: 0, skipped: true, reason: 'missing_site' },
      error: null,
    };
  }

  const itemsRes = await supabase
    .from('sale_items')
    .select('id, product_id, variant_id, quantity, combo_role')
    .eq('sale_id', order.sale_id);

  if (itemsRes.error) {
    return { data: { siteId, restoredItems: 0 }, error: itemsRes.error };
  }

  let restoredItems = 0;

  // Talles y colores: las líneas de productos con stock por talle se reponen a
  // la variante vendida (fn_mover_stock, "Sin clasificar" si la venta no tenía
  // variante). El resto sigue exactamente el camino de siempre.
  const restorableLines = (itemsRes.data || []).filter((item) => item?.product_id && item.combo_role !== 'component');
  const restorableProductIds = [...new Set(restorableLines.map((item) => item.product_id))];
  let variantTrackedIds = new Set();
  if (restorableProductIds.length > 0) {
    const productsRes = await supabase
      .from('products')
      .select('id, stock_tracking')
      .in('id', restorableProductIds);
    if (productsRes.error) {
      // No se pudo saber qué productos van por talle: se sigue con el camino
      // clásico para todos, como antes de talles y colores.
      console.error('No se pudo leer stock_tracking para reponer el pedido web:', productsRes.error);
    } else {
      variantTrackedIds = new Set(
        (productsRes.data || []).filter((product) => product?.stock_tracking === 'variant').map((product) => product.id),
      );
    }
  }
  let lineasReposicion = restorableLines;
  const lineasConTalle = restorableLines.filter((item) => variantTrackedIds.has(item.product_id));
  if (lineasConTalle.length > 0) {
    try {
      const cambios = await fetchCambiosTalle(supabase, lineasConTalle.map((item) => item.id));
      if (cambios.length > 0) {
        lineasReposicion = redistribuirPorCambiosTalle(lineasConTalle.map((item) => ({ ...item, sale_item_id: item.id })), cambios);
      }
    } catch (cambiosError) {
      return { data: { siteId, restoredItems }, error: cambiosError };
    }
  }
  const { variantItems } = splitVariantItems({ items: lineasReposicion, variantTrackedIds });
  if (variantItems.length > 0) {
    try {
      const { restored } = await restoreVariantStock({
        supabase,
        organizationId,
        siteId,
        items: variantItems,
        motivo: `Anulación Venta Web #${order.tracking_code || order.id?.slice?.(0, 8) || 'PEDIDO'}`,
        canal: 'tienda',
      });
      restoredItems += restored;
    } catch (variantError) {
      return { data: { siteId, restoredItems }, error: variantError };
    }
  }

  for (const item of itemsRes.data || []) {
    if (variantTrackedIds.has(item?.product_id)) continue;
    if (!item?.product_id) continue;

    // El checkout descuenta stock SOLO de las líneas vendibles del carrito
    // (productos simples y el 'parent' del combo), nunca de los componentes.
    // Reponer también los componentes inflaría el stock en cada cancelación.
    if (item.combo_role === 'component') continue;

    // Reposición ATÓMICA vía RPC (un solo UPDATE con lock de fila), igual que el
    // descuento en el checkout. Fallback legacy si la migración no se ejecutó.
    const { error: rpcError } = await supabase.rpc('increment_store_stock', {
      p_organization_id: organizationId,
      p_product_id: item.product_id,
      p_site_id: siteId,
      p_quantity: Number(item.quantity || 0),
    });

    if (rpcError) {
      if (rpcError.code !== '42883' && !/does not exist/i.test(rpcError.message || '')) {
        return { data: { siteId, restoredItems }, error: rpcError };
      }

      const stockRes = await supabase
        .from('stock_levels')
        .select('id, quantity')
        .eq('product_id', item.product_id)
        .eq('site_id', siteId)
        .maybeSingle();

      if (stockRes.error) {
        return { data: { siteId, restoredItems }, error: stockRes.error };
      }

      const nextQty = Number(stockRes.data?.quantity || 0) + Number(item.quantity || 0);

      if (stockRes.data?.id) {
        const updateRes = await supabase
          .from('stock_levels')
          .update({ quantity: nextQty, updated_at: new Date().toISOString() })
          .eq('id', stockRes.data.id);

        if (updateRes.error) {
          return { data: { siteId, restoredItems }, error: updateRes.error };
        }
      } else {
        const insertRes = await supabase
          .from('stock_levels')
          .insert({
            organization_id: organizationId,
            product_id: item.product_id,
            site_id: siteId,
            quantity: nextQty,
          });

        if (insertRes.error) {
          return { data: { siteId, restoredItems }, error: insertRes.error };
        }
      }
    }

    const movementRes = await supabase
      .from('movimientos_stock')
      .insert({
        organization_id: organizationId,
        producto_id: item.product_id,
        tipo: 'Ingreso',
        cantidad: Number(item.quantity || 0),
        motivo: `Anulación Venta Web #${order.tracking_code || order.id?.slice?.(0, 8) || 'PEDIDO'}`,
        sitio_destino: siteId,
      });

    if (movementRes.error) {
      return { data: { siteId, restoredItems }, error: movementRes.error };
    }

    restoredItems += 1;
  }

  return {
    data: { siteId, restoredItems, skipped: restoredItems === 0 },
    error: null,
  };
};
